import { useState } from 'react';
import { isStringId, useSessionIdSet } from '@/hooks/useSessionIdSet';

/** Countries the user collapsed while a given search query is active. */
interface SearchCollapsed {
  query: string;
  keys: ReadonlySet<string>;
}

const NO_KEYS: ReadonlySet<string> = new Set();

export interface CountryExpansion {
  isExpanded: (key: string) => boolean;
  toggle: (key: string) => void;
  /** Expands (`expand = true`) or collapses every key in `keys`. */
  setAll: (keys: readonly string[], expand: boolean) => void;
}

/**
 * Expanded state of country cards. Without a search the expanded keys persist
 * per browser tab (sessionStorage; everything starts collapsed). While
 * `activeQuery` is non-empty every visible country is expanded and toggles only
 * go to a transient per-query set, so the persisted state is never touched.
 */
export function useCountryExpansion(storageKey: string, activeQuery: string): CountryExpansion {
  const expanded = useSessionIdSet(storageKey, isStringId);
  const [searchCollapsed, setSearchCollapsed] = useState<SearchCollapsed>({
    query: '',
    keys: NO_KEYS,
  });
  const collapsedInSearch = searchCollapsed.query === activeQuery ? searchCollapsed.keys : NO_KEYS;

  const isExpanded = (key: string) => (activeQuery
    ? !collapsedInSearch.has(key)
    : expanded.ids.has(key));

  const toggle = (key: string) => {
    if (!activeQuery) {
      expanded.toggle(key);
      return;
    }
    const keys = new Set(collapsedInSearch);
    if (keys.has(key)) keys.delete(key);
    else keys.add(key);
    setSearchCollapsed({ query: activeQuery, keys });
  };

  const setAll = (keys: readonly string[], expand: boolean) => {
    if (!activeQuery) {
      expanded.setMany(keys, expand);
      return;
    }
    const next = new Set(collapsedInSearch);
    for (const key of keys) {
      if (expand) next.delete(key);
      else next.add(key);
    }
    setSearchCollapsed({ query: activeQuery, keys: next });
  };

  return { isExpanded, toggle, setAll };
}
